import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, BoxList, Button } from '@factorialco/deprecated-design-system'
import { OneEmptyState } from '@factorialco/factorial-one-react/dist/experimental'
import { WorkSpacesDocument } from 'generated/resources/graphql'

import Loading from 'modules/core/components/Loading'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { WorkSpace } from 'modules/space_control/hooks/useWorkSpace'

import WorkSpaceItem from './WorkSpaceItem'

interface Props {
  workAreaId: number
  locationId: number
  selectedWorkSpace?: WorkSpace | null
  hoveredWorkSpaceId?: number | null
  onWorkSpaceSelect?: (workSpace: WorkSpace | null) => void
}

const LocationWorkSpaces = ({
  workAreaId,
  locationId,
  selectedWorkSpace,
  hoveredWorkSpaceId,
  onWorkSpaceSelect,
}: Props) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const { data, loading } = useQuery(WorkSpacesDocument, {
    variables: { locationIds: [locationId], workAreaIds: [workAreaId] },
  })

  const workSpaces = data?.spaceControl.workSpacesConnection.nodes ?? []
  const canAddSpace = data?.spaceControl._policies.workSpaceCreate.can ?? false

  const addSpaceClick = () => {
    track(trackingEvents.spaceControl.addSpace, {})
    navigate(
      navigation.settings.locations.locationsId.workAreas.workSpaces.addWorkSpace.fullPath({
        id: locationId,
        workAreaId: workAreaId,
      })
    )
  }

  const handleWorkSpaceSelect = (workSpace: WorkSpace) => {
    if (onWorkSpaceSelect) {
      onWorkSpaceSelect(selectedWorkSpace?.id === workSpace.id ? null : workSpace)
    }
  }

  if (loading) {
    return (
      <Box padding="s16">
        <Loading />
      </Box>
    )
  }

  if (workSpaces.length === 0) {
    return (
      <Box width="s400">
        <OneEmptyState
          title={i18n.t('settings.locations.location.work_spaces.empty_message')}
          description=""
          emoji={'🏢'}
          actions={
            canAddSpace
              ? [
                  {
                    label: i18n.t('settings.locations.location.work_areas.add_space'),
                    onClick: addSpaceClick,
                    type: 'default',
                    variant: 'outline',
                  },
                ]
              : []
          }
        />
      </Box>
    )
  }

  return (
    <Box paddingBottom="s12" gap="s12">
      {canAddSpace && (
        <Button onClick={addSpaceClick}>
          {i18n.t('settings.locations.location.work_areas.add_space')}
        </Button>
      )}
      <BoxList>
        {workSpaces.map((workSpace: WorkSpace) => (
          <WorkSpaceItem
            key={`${workSpace.id}${workSpace.name}`}
            workSpace={workSpace}
            locationId={locationId}
            workAreaId={workAreaId}
            isHighlighted={
              selectedWorkSpace?.id === workSpace.id || hoveredWorkSpaceId === workSpace.id
            }
            onSelect={handleWorkSpaceSelect}
          />
        ))}
      </BoxList>
    </Box>
  )
}

export default LocationWorkSpaces
